/**
 * Team & Roles React Query Hooks
 * Phase 11: User Roles & Permissions
 *
 * Provides hooks for team member management, role assignment,
 * invitations, and permission checking.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  QmsRole,
  TeamMember,
  UserRole,
  UserRoleInsert,
  UserRoleUpdate,
  TeamInvitation,
  TeamInvitationInsert,
  Permission,
  PermissionAuditLog,
  NotificationPreferences,
  NotificationPreferencesUpdate,
  TeamStats,
} from '@/types/qms'
import * as teamService from '@/services/teamService'

// ============================================
// QUERY KEYS
// ============================================

export const teamKeys = {
  all: ['team'] as const,
  members: () => [...teamKeys.all, 'members'] as const,
  member: (id: string) => [...teamKeys.members(), id] as const,
  stats: () => [...teamKeys.all, 'stats'] as const,
}

export const roleKeys = {
  all: ['roles'] as const,
  list: () => [...roleKeys.all, 'list'] as const,
  user: (userId: string) => [...roleKeys.all, 'user', userId] as const,
  current: () => [...roleKeys.all, 'current'] as const,
}

export const invitationKeys = {
  all: ['invitations'] as const,
  list: () => [...invitationKeys.all, 'list'] as const,
  pending: () => [...invitationKeys.all, 'pending'] as const,
}

export const permissionKeys = {
  all: ['permissions'] as const,
  list: () => [...permissionKeys.all, 'list'] as const,
  role: (role: QmsRole) => [...permissionKeys.all, 'role', role] as const,
  check: (code: string) => [...permissionKeys.all, 'check', code] as const,
  auditLog: () => [...permissionKeys.all, 'auditLog'] as const,
}

export const notificationKeys = {
  all: ['notifications'] as const,
  preferences: (userId?: string) =>
    [...notificationKeys.all, 'preferences', userId || 'current'] as const,
}

// ============================================
// TEAM MEMBER HOOKS
// ============================================

/**
 * Get all team members with their roles
 */
export function useTeamMembers() {
  return useQuery<TeamMember[], Error>({
    queryKey: teamKeys.members(),
    queryFn: teamService.getTeamMembers,
  })
}

/**
 * Get a single team member
 */
export function useTeamMember(userId: string) {
  return useQuery<TeamMember | null, Error>({
    queryKey: teamKeys.member(userId),
    queryFn: () => teamService.getTeamMember(userId),
    enabled: !!userId,
  })
}

/**
 * Get team statistics
 */
export function useTeamStats() {
  return useQuery<TeamStats, Error>({
    queryKey: teamKeys.stats(),
    queryFn: teamService.getTeamStats,
  })
}

// ============================================
// USER ROLE HOOKS
// ============================================

/**
 * Get all user roles
 */
export function useUserRoles() {
  return useQuery<UserRole[], Error>({
    queryKey: roleKeys.list(),
    queryFn: teamService.getUserRoles,
  })
}

/**
 * Get a user's role
 */
export function useUserRole(userId: string) {
  return useQuery<UserRole | null, Error>({
    queryKey: roleKeys.user(userId),
    queryFn: () => teamService.getUserRole(userId),
    enabled: !!userId,
  })
}

/**
 * Get the current user's QMS role
 */
export function useCurrentUserRole() {
  return useQuery<QmsRole | null, Error>({
    queryKey: roleKeys.current(),
    queryFn: teamService.getCurrentUserRole,
  })
}

/**
 * Assign a role to a user
 */
export function useAssignRole() {
  const queryClient = useQueryClient()

  return useMutation<UserRole, Error, UserRoleInsert>({
    mutationFn: teamService.assignUserRole,
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: teamKeys.members() })
      queryClient.invalidateQueries({ queryKey: roleKeys.list() })
      queryClient.invalidateQueries({ queryKey: roleKeys.user(variables.user_id) })
      queryClient.invalidateQueries({ queryKey: teamKeys.stats() })
      queryClient.invalidateQueries({ queryKey: permissionKeys.auditLog() })
    },
  })
}

/**
 * Update a user's role
 */
export function useUpdateRole() {
  const queryClient = useQueryClient()

  return useMutation<
    UserRole,
    Error,
    { roleId: string; updates: UserRoleUpdate }
  >({
    mutationFn: ({ roleId, updates }) =>
      teamService.updateUserRole(roleId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.members() })
      queryClient.invalidateQueries({ queryKey: roleKeys.list() })
      queryClient.invalidateQueries({ queryKey: teamKeys.stats() })
      queryClient.invalidateQueries({ queryKey: permissionKeys.auditLog() })
    },
  })
}

/**
 * Remove a user's role
 */
export function useRemoveRole() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: teamService.removeUserRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.members() })
      queryClient.invalidateQueries({ queryKey: roleKeys.list() })
      queryClient.invalidateQueries({ queryKey: teamKeys.stats() })
      queryClient.invalidateQueries({ queryKey: permissionKeys.auditLog() })
    },
  })
}

/**
 * Remove a user from the team entirely (org + role)
 */
export function useRemoveFromTeam() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: teamService.removeFromTeam,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.members() })
      queryClient.invalidateQueries({ queryKey: roleKeys.list() })
      queryClient.invalidateQueries({ queryKey: teamKeys.stats() })
      queryClient.invalidateQueries({ queryKey: permissionKeys.auditLog() })
    },
  })
}


export function useUpdateMemberDisplayName() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, { userId: string; fullName: string }>({
    mutationFn: ({ userId, fullName }) => teamService.updateMemberDisplayName(userId, fullName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.members() })
      queryClient.invalidateQueries({ queryKey: permissionKeys.auditLog() })
    },
  })
}

// ============================================
// INVITATION HOOKS
// ============================================

/**
 * Get all invitations
 */
export function useTeamInvitations() {
  return useQuery<TeamInvitation[], Error>({
    queryKey: invitationKeys.list(),
    queryFn: teamService.getTeamInvitations,
  })
}

/**
 * Get pending invitations only
 */
export function usePendingInvitations() {
  return useQuery<TeamInvitation[], Error>({
    queryKey: invitationKeys.pending(),
    queryFn: teamService.getPendingInvitations,
  })
}

/**
 * Create a team invitation
 */
export function useCreateInvitation() {
  const queryClient = useQueryClient()

  return useMutation<TeamInvitation, Error, TeamInvitationInsert>({
    mutationFn: teamService.createTeamInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invitationKeys.all })
      queryClient.invalidateQueries({ queryKey: teamKeys.stats() })
    },
  })
}

/**
 * Cancel an invitation
 */
export function useCancelInvitation() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: teamService.cancelInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invitationKeys.all })
      queryClient.invalidateQueries({ queryKey: teamKeys.stats() })
    },
  })
}

/**
 * Resend an invitation
 */
export function useResendInvitation() {
  const queryClient = useQueryClient()

  return useMutation<TeamInvitation, Error, string>({
    mutationFn: teamService.resendInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invitationKeys.all })
    },
  })
}

/**
 * Accept an invitation
 */
export function useAcceptInvitation() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: teamService.acceptInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invitationKeys.all })
      queryClient.invalidateQueries({ queryKey: teamKeys.all })
      queryClient.invalidateQueries({ queryKey: roleKeys.all })
    },
  })
}

// ============================================
// PERMISSION HOOKS
// ============================================

/**
 * Get all permissions
 */
export function usePermissions() {
  return useQuery<Permission[], Error>({
    queryKey: permissionKeys.list(),
    queryFn: teamService.getPermissions,
  })
}

/**
 * Get permissions for a specific role
 */
export function useRolePermissions(role: QmsRole) {
  return useQuery<Permission[], Error>({
    queryKey: permissionKeys.role(role),
    queryFn: () => teamService.getRolePermissions(role),
    enabled: !!role,
  })
}

/**
 * Check if user has a specific permission
 * This is a frequently used hook for conditional rendering
 */
export function useHasPermission(permissionCode: string) {
  return useQuery({
    queryKey: permissionKeys.check(permissionCode),
    queryFn: () => teamService.checkPermission(permissionCode),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    enabled: !!permissionCode,
  })
}

/**
 * Check multiple permissions at once
 */
export function useHasPermissions(permissionCodes: string[]) {
  return useQuery({
    queryKey: [...permissionKeys.all, 'check-multiple', [...permissionCodes].sort().join(',')],
    queryFn: () => teamService.checkPermissions(permissionCodes),
    staleTime: 5 * 60 * 1000,
    enabled: permissionCodes.length > 0,
  })
}

/**
 * Get permission audit log
 */
export function usePermissionAuditLog(options?: {
  limit?: number
  targetUserId?: string
}) {
  return useQuery<PermissionAuditLog[], Error>({
    queryKey: [...permissionKeys.auditLog(), options],
    queryFn: () => teamService.getPermissionAuditLog(options),
  })
}

// ============================================
// NOTIFICATION PREFERENCE HOOKS
// ============================================

/**
 * Get notification preferences
 */
export function useNotificationPreferences(userId?: string) {
  return useQuery<NotificationPreferences | null, Error>({
    queryKey: notificationKeys.preferences(userId),
    queryFn: () => teamService.getNotificationPreferences(userId),
  })
}

/**
 * Update notification preferences
 */
export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient()

  return useMutation<NotificationPreferences, Error, NotificationPreferencesUpdate>({
    mutationFn: teamService.updateNotificationPreferences,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

// ============================================
// UTILITY HOOKS
// ============================================

/**
 * Check if current user can assign a role
 */
export function useCanAssignRole(targetRole: QmsRole) {
  const { data: currentRole } = useCurrentUserRole()

  return {
    canAssign: teamService.canAssignRole(currentRole ?? null, targetRole),
    currentRole,
    targetRole,
  }
}

/**
 * Get role hierarchy comparison
 */
export function useRoleComparison(role1: QmsRole, role2: QmsRole) {
  const level1 = teamService.getRoleLevel(role1)
  const level2 = teamService.getRoleLevel(role2)

  return {
    role1Level: level1,
    role2Level: level2,
    isHigher: level1 > level2,
    isEqual: level1 === level2,
    isLower: level1 < level2,
  }
}
